import React, { Fragment, PureComponent } from 'react';
import Collapse from '@material-ui/core/Collapse';
import styled from 'styled-components';
import Location from '@solvhealth/types/interfaces/Location';
import StarSelector from '../../SolvPatternLibrary/StarSelector';
import { colors } from '../../../constants/colors';
import { fontSize, fontFamily } from '../../../constants/text';
import { biggerThanOrEqualTo, smallerThanOrEqualTo } from '../../../core/util/styledComponents';
import { PRIMARY_RATING } from './PrimaryRatingSelector';
import { isTelemedLocation } from '../../../core/util/location';
import {
  BEDSIDE_MANNER,
  FACILITY_CLEANLINESS,
  QUALITY_OF_CARE,
  STAFF_FRIENDLINESS,
  WAIT_TIME,
} from '../constants';

const Divider = styled.div`
  height: 1px;
  background-color: ${colors.dorianGrey};
  display: block;
  margin: 0 20px;

  ${biggerThanOrEqualTo.sm`
    width: 100%;
    margin: 0 auto;
  `}
`;

const Root = styled.div`
  padding: 26px 20px;
  text-align: center;
`;

const Title = styled.h1`
  margin: 0 auto 20px;
  display: block;
  font-family: ${fontFamily.bold};
  font-size: ${fontSize.large};
  color: ${colors.blueMonday};

  ${smallerThanOrEqualTo.sm`
    font-size: ${fontSize.medium};
  `}
`;

const RatingContainer = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 475px;
  margin: 0 auto;
  padding-bottom: 20px;

  ${biggerThanOrEqualTo.sm`
    padding-bottom: 30px;
  `}
`;

const RatingCategory = styled.h2`
  display: block;
  font-family: ${fontFamily.bold};
  font-size: ${fontSize.default};
  color: ${colors.blueMonday};
  align-self: center;

  ${smallerThanOrEqualTo.sm`
    font-size: ${fontSize.mediumSmall};
  `}
`;

const CenteredStars = styled(StarSelector)`
  align-self: center;
`;

const SECONDARY_RATINGS = [
  WAIT_TIME,
  QUALITY_OF_CARE,
  BEDSIDE_MANNER,
  STAFF_FRIENDLINESS,
  FACILITY_CLEANLINESS,
];

const TELEMED_SECONDARY_RATINGS = SECONDARY_RATINGS.slice(0, SECONDARY_RATINGS.length - 1);

const getDefaultRatings = (location: Location) =>
  isTelemedLocation(location) ? TELEMED_SECONDARY_RATINGS : SECONDARY_RATINGS;

type Props = {
  leaveAReview?: any;
  onChange?: (...args: any[]) => any;
  location: Location;
  ratings?: string[];
};

const SecondaryStarSelectors = (props: Props) => {
  const { leaveAReview, onChange, location } = props;
  const { ratings = getDefaultRatings(location) } = props;

  const renderDimensions = (secondaryRatings: any, leaveAReview: any, onChange: any) =>
    secondaryRatings.map((genre: any) => (
      <RatingContainer key={genre}>
        <RatingCategory>{genre}</RatingCategory>

        <CenteredStars
          onChange={(value: any) => onChange({ value, genre })}
          rating={leaveAReview[genre]}
          starWidth={29}
        />
      </RatingContainer>
    ));

  return (
    <Collapse in={!!leaveAReview[PRIMARY_RATING]} timeout="auto">
      <>
        <Divider />

        <Root>
          <Title>How would you rate the following?</Title>
          {renderDimensions(ratings, leaveAReview, onChange)}
        </Root>
      </>
    </Collapse>
  );
};

export default SecondaryStarSelectors;
